/**
 * GamificationSettings — Gamification stats, controls, and onboarding
 * re-run for the Settings page.
 *
 * Shows: streak stats, quest progress, badge count, re-run onboarding
 * button, and a reset gamification data option.
 *
 * Self-contained — reads/writes through useApp().
 */
import { useMemo } from "react";
import { Card } from '../../components/Card';
import { Heading } from '../../components/Heading';
import { useApp } from '../../hooks/useApp';
import { STREAK_DEFAULTS, getStreakTier } from '../streaks/streakEngine';
import { QUESTS } from '../quests/questConfig';
import { BADGES } from '../rewards/badgeConfig';

export default function GamificationSettings() {
  const { s, c, up, upM, showToast, setConfirm } = useApp();

  /* ─── Stats ─── */
  const streak = s.streak || STREAK_DEFAULTS;
  const { emoji } = getStreakTier(streak.current);
  const questsCompleted = Object.keys(s.questsCompleted || {}).length;
  const questsTotal = QUESTS.length;
  const badgesEarned = BADGES.filter((b) => b.questId in (s.questsCompleted || {})).length;
  const badgesTotal = BADGES.length;
  const totalCheckIns = (streak.history || []).length;

  /* ─── Onboarding ─── */
  const handleRerunOnboarding = () => {
    setConfirm({
      msg: "This will re-run the onboarding wizard. Your current settings will be replaced with whatever you enter. Continue?",
      onOk: () => {
        up("onboarded", false);
        setConfirm(null);
        showToast("Starting onboarding...", "info");
      },
    });
  };

  /* ─── Reset gamification ─── */
  const handleResetGamification = () => {
    setConfirm({
      msg: "This will reset your streaks, quest completions, and badges to zero. Your financial settings won't be touched. This cannot be undone.",
      onOk: () => {
        upM({
          streak: STREAK_DEFAULTS,
          questsCompleted: {},
        });
        setConfirm(null);
        showToast("Gamification data reset", "info");
      },
    });
  };

  return (
    <Card c={c} delay={0.22}>
      <Heading ic={null} c={c}>
        <span style={{ fontSize: 17 }}>🎮</span> Gamification
      </Heading>

      {/* Stats grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
        gap: 10, marginBottom: 20,
      }}>
        <StatTile
          label="Current Streak"
          value={`${streak.current} ${emoji}`}
          sub={streak.best > 0 ? `Best: ${streak.best}` : null}
          c={c} s={s}
        />
        <StatTile
          label="Check-ins"
          value={totalCheckIns}
          sub="Last 30 days"
          c={c} s={s}
        />
        <StatTile
          label="Quests"
          value={`${questsCompleted}/${questsTotal}`}
          sub={questsCompleted === questsTotal ? "All complete!" : `${questsTotal - questsCompleted} remaining`}
          c={c} s={s}
        />
        <StatTile
          label="Badges"
          value={`${badgesEarned}/${badgesTotal}`}
          sub={badgesEarned === badgesTotal ? "Full collection" : `${badgesTotal - badgesEarned} locked`}
          c={c} s={s}
        />
      </div>

      {/* Budget streak */}
      {streak.budgetStreak > 0 && (
        <div style={{
          padding: "10px 16px", borderRadius: 12, marginBottom: 16,
          background: s.dark ? "rgba(48,209,88,0.08)" : "rgba(48,209,88,0.04)",
          fontSize: 13, color: c.green, fontWeight: 600,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          🎯 {streak.budgetStreak}-day budget compliance streak
        </div>
      )}

      {/* Actions */}
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap",
        paddingTop: 4,
      }}>
        <SettingsAction
          label="Re-run Onboarding"
          icon="🔄"
          onClick={handleRerunOnboarding}
          c={c} s={s}
        />
        <SettingsAction
          label="Reset Gamification"
          icon="🗑️"
          onClick={handleResetGamification}
          danger
          c={c} s={s}
        />
      </div>

      {/* First check-in date */}
      {streak.history && streak.history.length > 0 && (
        <div style={{
          marginTop: 16, fontSize: 12, color: c.muted, opacity: 0.6,
        }}>
          First check-in: {streak.history[0]}
        </div>
      )}
    </Card>
  );
}


/* ─── Small stat tile ─── */
function StatTile({ label, value, sub, c, s }) {
  return (
    <div style={{
      padding: "12px 14px", borderRadius: 12,
      background: s.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)",
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600, color: c.muted,
        letterSpacing: "0.08em", textTransform: "uppercase",
        marginBottom: 5,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 20, fontWeight: 700, color: c.fg,
        letterSpacing: "-0.02em",
      }}>
        {value}
      </div>
      {sub && (
        <div style={{
          fontSize: 11, color: c.muted, marginTop: 3, opacity: 0.7,
        }}>
          {sub}
        </div>
      )}
    </div>
  );
}


/* ─── Action button ─── */
function SettingsAction({ label, icon, onClick, danger, c, s }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "9px 18px", borderRadius: 12,
        border: `1px solid ${danger ? c.red : c.border}`,
        background: "transparent",
        color: danger ? c.red : c.fg,
        fontSize: 13, fontWeight: 600, cursor: "pointer",
        transition: "all 0.2s cubic-bezier(0.25,0.1,0.25,1)",
      }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      {label}
    </button>
  );
}
